import { Platform } from "react-native";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";
import { modalStackAtom, navModalStackAtom, generateNavModalId } from "../atoms/modal";
import {
    getModalStackPosition,
    getModalStackSlot,
    WEB_DIALOG_ANIMATION_DURATION,
} from "../utils/modal-stack-slot";

const IDENTITY_SLOT = { translateY: 0, scale: 1 };

/**
 * Web-only transform for a navigation modal's card so it stacks in the SAME iOS-sheet window as every
 * other modal — they live in different rendering engines (react-navigation cards, ModalPortal JS
 * modals) and don't share a stack, so without this the card behind wouldn't move.
 *
 * A navigation modal sits below every JS modal AND below every navigation modal opened after it, so
 * its depth-from-top is (nav modals stacked above it) + (visible JS modals). It then uses the shared
 * getModalStackSlot geometry: 0 → front slot (a small downward peek); 1 → receded behind the modal on
 * top; ≥2 → slid back down, hidden behind the opaque front modal (so opening a 3rd modal — whether a
 * JS modal or another nav modal like SendInvitation/IJM/SalaryCountingDetails — makes this one
 * "descend" out of view).
 *
 * No-op on native (identity transform): there modals are real RN Modals and the OS handles stacking.
 */
export const useModalRecedeStyle = () => {
    const jsStack = useAtomValue(modalStackAtom);
    const [navStack, setNavStack] = useAtom(navModalStackAtom);
    const [id] = useState(generateNavModalId);
    const isWeb = Platform.OS === "web";

    // Register this nav modal on mount so its siblings can count how many nav modals sit above it
    // (mount order == stack order); remove it on unmount (going back / closing).
    useEffect(() => {
        if (!isWeb) {
            return;
        }
        setNavStack((prev) => (prev.includes(id) ? prev : [...prev, id]));
        return () => setNavStack((prev) => prev.filter((x) => x !== id));
    }, [isWeb, setNavStack, id]);

    // This nav modal's place in the single conceptual stack: nav modals fill the bottom (mount order),
    // every JS modal is above them. Before it has registered (index -1) it is the top-most nav modal
    // (nothing above it yet). getModalStackPosition is the SAME rule the JS modals use (ModalPortal).
    const myNavIndex = navStack.indexOf(id);
    const registered = myNavIndex !== -1;
    const jsVisibleCount = jsStack.filter((m) => m.visible).length;
    const navTotal = registered ? navStack.length : navStack.length + 1;
    const { depthFromTop } = getModalStackPosition(
        registered ? myNavIndex : navStack.length,
        navTotal + jsVisibleCount,
    );
    const slot = isWeb ? getModalStackSlot(depthFromTop) : IDENTITY_SLOT;

    const translateY = useSharedValue(slot.translateY);
    const scale = useSharedValue(slot.scale);
    useEffect(() => {
        translateY.value = withTiming(slot.translateY, {
            duration: WEB_DIALOG_ANIMATION_DURATION,
        });
        scale.value = withTiming(slot.scale, { duration: WEB_DIALOG_ANIMATION_DURATION });
    }, [slot.translateY, slot.scale, translateY, scale]);

    return useAnimatedStyle(() => ({
        transform: [{ translateY: translateY.value }, { scale: scale.value }],
    }));
};
